import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileSpreadsheet, Scale, Calculator, BookOpen, BarChart3, Settings, LogOut, Menu, Wallet, FileCheck2, Banknote, Receipt, HandCoins, KeyRound } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, ADVANCE_ONLY } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MyAccount } from "@/pages/Settings";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/employees", label: "Employees", icon: Users, id: "employees" },
  { to: "/import", label: "Excel Import", icon: FileSpreadsheet, id: "import", roles: ["admin"] },
  { to: "/opening-balances", label: "Opening Balances", icon: Scale, id: "opening" },
  { to: "/add-advance", label: "Add Advance", icon: HandCoins, id: "add-advance" },
  { to: "/deduction", label: "Advance Deduction", icon: Calculator, id: "deduction" },
  { to: "/finalize-advance", label: "Finalize Advance", icon: FileCheck2, id: "finalize-advance" },
  { to: "/finalize-payroll", label: "Finalize Payroll", icon: Banknote, id: "finalize-payroll" },
  { to: "/ledger", label: "Employee Ledger", icon: BookOpen, id: "ledger" },
  { to: "/payslips", label: "Payslips", icon: Receipt, id: "payslips" },
  { to: "/salary", label: "Salary Calculator", icon: Wallet, id: "salary" },
  { to: "/reports", label: "Reports", icon: BarChart3, id: "reports" },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
];

function Brand() {
  return (
    <div className="px-5 py-5 border-b border-white/10">
      <div className="rounded-lg bg-white px-3 py-2.5">
        <img src="/bshpl-logo.png" alt="BSHPL logo" className="h-8 w-auto" data-testid="sidebar-logo" />
      </div>
      <div className="mt-2.5 leading-tight">
        <div className="font-heading font-bold text-white text-[14px]">Balaji Sweet House Pvt. Ltd.</div>
        <div className="text-[11px] text-slate-400 tracking-wide">Employee Advance Ledger</div>
      </div>
    </div>
  );
}

function NavItems({ onNavigate }) {
  const { user } = useAuth();
  return (
    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
      {NAV.filter((n) => (ADVANCE_ONLY.includes(user.role) ? n.id === "add-advance" : !n.roles || n.roles.includes(user.role))).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          onClick={onNavigate}
          data-testid={`nav-${n.id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
              isActive ? "bg-blue-700 text-white shadow-sm" : "text-slate-300 hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <n.icon className="h-[18px] w-[18px] shrink-0" />
          <span className="flex-1">{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function UserBox() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState(false);
  return (
    <div className="p-4 border-t border-white/10">
      <div className="text-sm text-white font-medium truncate" data-testid="current-user-name">{user.name}</div>
      <div className="text-xs text-slate-400 truncate">{user.email}</div>
      {ADVANCE_ONLY.includes(user.role) && (
        <>
          <button onClick={() => setPw(true)} className="mt-1.5 text-xs text-blue-200 hover:text-white flex items-center gap-1 transition-colors" data-testid="sidebar-change-password-btn"><KeyRound className="h-3.5 w-3.5" /> Change password</button>
          <Dialog open={pw} onOpenChange={setPw}>
            <DialogContent className="max-w-lg" data-testid="change-password-dialog">
              <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
              <MyAccount />
            </DialogContent>
          </Dialog>
        </>
      )}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[11px] rounded-full bg-white/10 text-blue-200 px-2 py-0.5" data-testid="current-user-role">{ROLE_LABEL[user.role]}</span>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="text-xs text-slate-300 hover:text-white flex items-center gap-1 transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" /> Logout
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex">
      <aside className="no-print hidden lg:flex w-64 shrink-0 flex-col bg-slate-900 fixed inset-y-0 left-0 z-30">
        <Brand />
        <NavItems />
        <UserBox />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-slate-900 border-slate-800 flex flex-col">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Brand />
          <NavItems onNavigate={() => setOpen(false)} />
          <UserBox />
        </SheetContent>
      </Sheet>
      <div className="flex-1 lg:pl-64 min-w-0">
        <header className="no-print lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 h-14 flex items-center px-3 gap-2">
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </Button>
          <img src="/bshpl-logo.png" alt="BSHPL logo" className="h-6 w-auto" data-testid="mobile-header-logo" />
          <div className="font-heading font-semibold text-slate-600 text-sm">Advance Ledger</div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{children}</main>
      </div>
    </div>
  );
}
